import type {
  BanquestWebhookRegistrationMode,
  PaymentModuleId,
} from '@triedge/domain-core';
import type { BanquestControlPlaneGuideProps } from '../types';

const readText = (value: unknown, fallback = ''): string => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const formatTimestamp = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return new Date(value * 1000).toLocaleString();
  }
  const next = readText(value);
  if (!next) {
    return 'Not available';
  }
  const parsed = new Date(next);
  return Number.isNaN(parsed.getTime()) ? next : parsed.toLocaleString();
};

const formatBoolean = (value: boolean): string => (value ? 'Yes' : 'No');

const formatRegistrationMode = (value: BanquestWebhookRegistrationMode): string =>
  value === 'api_managed' ? 'API managed' : 'Manual';

const formatModuleLabel = (value: PaymentModuleId): string => {
  switch (value) {
    case 'tip-a-teacher':
      return 'Tip a Teacher';
    default:
      return value.charAt(0).toUpperCase() + value.slice(1);
  }
};

const IMPORT_MODULES: PaymentModuleId[] = [
  'donations',
  'tuition',
  'tip-a-teacher',
  'camps',
  'enrollment',
];

const stageTone = (enabled: boolean): string =>
  enabled
    ? 'border-[#0F766E] bg-[#ECFDF5] text-[#065F46]'
    : 'border-[#D1D5DB] bg-[#F8FAFC] text-[#0F172A]';

export function BanquestControlPlaneGuide(props: BanquestControlPlaneGuideProps) {
  const { readiness, form } = props;
  const operationMessage = readText(props.messages?.operationMessage);
  const operationError = readText(props.messages?.operationError);
  const selectedModules = new Set(form.allowedImportModules);

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border px-4 py-4 ${stageTone(form.enabled)}`}>
        <p className="text-xs font-semibold uppercase tracking-[0.16em]">Banquest</p>
        <p className="mt-2 text-sm font-semibold">
          {form.enabled ? 'Banquest control plane enabled' : 'Banquest control plane disabled'}
        </p>
        <p className="mt-1 text-sm opacity-90">
          Settings owns configuration and readiness only in this wave. Webhook CRUD and transaction import stay outside the settings surface.
        </p>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Configuration
        </p>
        <label className="mt-3 flex items-start gap-3 rounded-md border border-[#D1D5DB] bg-[#F8FAFC] px-3 py-3">
          <input
            checked={form.enabled}
            onChange={(event) => props.onEnabledChange(event.currentTarget.checked)}
            type="checkbox"
            data-testid="banquest-control-plane-enabled-toggle"
          />
          <span className="block">
            <span className="block text-sm font-semibold text-[#111827]">Enable Banquest</span>
            <span className="mt-1 block text-xs text-[#475569]">
              When enabled, the backend will generate a tenant-scoped webhook path key if one does not exist yet.
            </span>
          </span>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm font-medium text-[#111827]">Registration mode</span>
          <select
            value={form.registrationMode}
            onChange={(event) => props.onRegistrationModeChange(event.currentTarget.value as BanquestWebhookRegistrationMode)}
            className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
            data-testid="banquest-control-plane-registration-mode-select"
          >
            <option value="manual">Manual</option>
            <option value="api_managed">API managed</option>
          </select>
        </label>

        <div className="mt-4">
          <p className="text-sm font-medium text-[#111827]">Allowed import modules</p>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            {IMPORT_MODULES.map((moduleId) => (
              <label
                key={moduleId}
                className="flex items-start gap-3 rounded-md border border-[#D1D5DB] bg-[#F8FAFC] px-3 py-3"
              >
                <input
                  checked={selectedModules.has(moduleId)}
                  onChange={(event) => props.onAllowedImportModuleToggle(moduleId, event.currentTarget.checked)}
                  type="checkbox"
                  data-testid={`banquest-control-plane-module-toggle-${moduleId}`}
                />
                <span className="block text-sm text-[#111827]">{formatModuleLabel(moduleId)}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-[#475569]">
            Transaction retrieval mode stays locked to curated import only. Feature APIs still do not get broad Banquest transaction search.
          </p>
        </div>

        <button
          type="button"
          onClick={props.onSave}
          disabled={Boolean(props.actions?.isSaving)}
          className="mt-4 rounded-md bg-[#1C3B3E] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="banquest-control-plane-save-button"
        >
          {props.actions?.isSaving ? 'Saving…' : 'Save Banquest Settings'}
        </button>

        {operationMessage ? (
          <p className="mt-3 rounded-md border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-sm text-[#166534]">
            {operationMessage}
          </p>
        ) : null}
        {operationError ? (
          <p className="mt-3 rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">
            {operationError}
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Readiness
        </p>
        <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          <DetailRow label="Retrieval mode" value={readiness.banquest.transactionRetrievalMode} />
          <DetailRow label="Webhook path key" value={readText(readiness.banquest.webhookPathKey, 'Not configured')} />
          <DetailRow label="Webhook URL" value={readText(readiness.banquest.webhookUrl, 'Not configured')} />
          <DetailRow label="Webhook ID" value={readiness.banquest.webhookId ?? 'Not configured'} />
          <DetailRow label="Webhook active" value={formatBoolean(readiness.banquest.webhookActive)} />
          <DetailRow label="Last sync" value={formatTimestamp(readiness.banquest.lastWebhookSyncAt)} />
          <DetailRow label="Environment" value={readText(readiness.banquest.environmentKind, 'Not configured')} />
          <DetailRow label="Source key" value={formatBoolean(readiness.banquest.hasSourceKeyConfigured)} />
          <DetailRow label="Source key pin" value={formatBoolean(readiness.banquest.hasSourceKeyPinConfigured)} />
          <DetailRow label="API credential" value={formatBoolean(readiness.banquest.hasApiCredentialConfigured)} />
          <DetailRow label="Webhook signature" value={formatBoolean(readiness.banquest.hasWebhookSignatureConfigured)} />
          <DetailRow label="Tokenization public key" value={formatBoolean(readiness.banquest.hasTokenizationPublicKeyConfigured)} />
          <DetailRow label="Tokenization script URL" value={readText(readiness.banquest.tokenizationScriptUrl, 'Not configured')} />
          <DetailRow label="API base URL" value={readText(readiness.banquest.apiBaseUrl, 'Not configured')} />
          <DetailRow label="Registration mode" value={formatRegistrationMode(readiness.banquest.registrationMode)} />
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#F1F5F9] py-2 last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">
        {label}
      </span>
      <span className="max-w-[65%] text-right text-sm text-[#0F172A]">
        {value}
      </span>
    </div>
  );
}
